<template>
  <v-card class="okr-chart-card" elevation="0">
    <v-card-text class="pa-4">
      <div v-if="elementVisible && hasData">
        <vue-apex-charts
          type="bar"
          height="340"
          :options="dashboardOptions.length > 0 ? dashboardOptions[0] : {}"
          :series="series"
        ></vue-apex-charts>
      </div>
      <div v-else class="okr-chart-card__empty">
        <v-icon color="grey lighten-1">mdi-chart-bar</v-icon>
        <span>No hay información para el año y la semana seleccionados.</span>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';

export default {
  name: 'CantidadRNRXArea',
  props: {
    datos: {
      type: Object,
      default: () => {}
    }
  },
  data: () => ({
    series: [],
    chartOptions: {
      grid: {
        show: true,
        borderColor: 'rgba(0,0,0,.3)',
        strokeDashArray: 2,
        padding: {
          left: 8,
          right: 34,
          bottom: 6
        }
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '48%',
          endingShape: 'flat'
        }
      },
      colors: ['#10b981', '#d90429'],
      fill: {
        type: 'solid',
        opacity: 1
      },
      chart: {
        stacked: true,
        toolbar: {
          show: false
        },
        sparkline: {
          enabled: false
        }
      },
      title: {
        text: 'Actividades por área',
        align: 'center',
        margin: 5,
        offsetX: 0,
        offsetY: 0,
        floating: false,
        style: {
          fontSize: '18px',
          fontWeight: 'normal',
          fontFamily: 'Roboto, Helvetica, Arial',
          color: 'rgba(0, 0, 0, 0.8)'
        }
      },
      legend: {
        show: true,
        position: 'top',
        fontSize: '14px',
        fontFamily: 'Roboto, Helvetica, Arial',
        markers: {
          width: 14,
          height: 14,
          strokeWidth: 0,
          strokeColor: '#fff',
          fillColors: undefined,
          radius: 12,
          customHTML: undefined,
          onClick: undefined,
          offsetX: 0,
          offsetY: 0
        },
        labels: {
          colors: '#424242',
          useSeriesColors: false
        },
        itemMargin: {
          horizontal: 15,
          vertical: 5
        },
        onItemHover: {
          highlightDataSeries: true
        },
        onItemClick: {
          toggleDataSeries: true
        }
      },
      dataLabels: {
        enabled: true,
        offsetX: -4,
        style: {
          fontSize: '11px',
          fontWeight: '700'
        },
        formatter: function(value) {
          return Number(value) || 0;
        }
      },
      markers: {
        size: 0
      },
      xaxis: {
        type: 'category',
        categories: [],
        labels: {
          trim: true,
          style: {
            cssClass: 'text-muted fill-color'
          }
        }
      },
      yaxis: {
        show: true,
        labels: {
          maxWidth: 150,
          style: {
            fontSize: '11px',
            cssClass: 'text-muted fill-color'
          },
          formatter: function(value) {
            return value;
          }
        }
      },
      stroke: {
        curve: 'straight',
        width: 0
      },
      tooltip: {
        theme: 'dark',
        x: {
          formatter: function(value) {
            return value;
          }
        },
        y: {
          formatter: function(value) {
            return `${Number(value) || 0} actividades`;
          }
        }
      }
    },
    dashboardOptions: [],
    elementVisible: false,
    hasData: false
  }),
  watch: {
    datos(val) {
      let areas = [];
      let realizadas = [];
      let norealizadas = [];
      this.dashboardOptions = [];
      if (val.totaltareasareas != undefined) {
        if (val.totaltareasareas.length > 0) {
          val.totaltareasareas.forEach((element) => {
            const subarea = this.formatAreaName(element.subarea || element.area || element.idsubarea || 'Sin area');
            const actividadesRealizadas = Number(element.actividadesrealizadas) || 0;
            const actividadesNoRealizadas = Number(element.actividadesnorealizadas) || 0;

            if (actividadesRealizadas + actividadesNoRealizadas <= 0) {
              return;
            }

            realizadas.push(actividadesRealizadas);
            norealizadas.push(actividadesNoRealizadas);
            areas.push(subarea);
          });
          const axisMax = this.getAxisMax(realizadas.map((value, index) => value + norealizadas[index]));
          this.dashboardOptions.push({
            ...this.chartOptions,
            ...{
              xaxis: {
                categories: areas,
                max: axisMax,
                tickAmount: 4,
                labels: {
                  trim: false,
                  style: {
                    fontSize: '10px',
                    fontWeight: 'bolder',
                    cssClass: 'text-muted fill-color'
                  }
                }
              }
            }
          });
          this.series = [
            {
              name: 'Realizadas',
              data: realizadas
            },
            {
              name: 'No realizadas',
              data: norealizadas
            }
          ];
          this.hasData = areas.length > 0;
        } else {
          this.series = [];
          this.hasData = false;
        }
        this.elementVisible = true;
      }
    }
  },
  mounted() {
    this.applyThemeColors();
  },
  methods: {
    applyThemeColors() {
      const primary = (this.$vuetify && this.$vuetify.theme.currentTheme.primary) || '#d90429';
      this.chartOptions = {
        ...this.chartOptions,
        colors: ['#10b981', primary]
      };
    },
    formatAreaName(value) {
      const text = String(value || 'Sin area')
        .trim()
        .toLocaleLowerCase('es-CO');
      return text.replace(/(^|\s|\/|-)\S/g, (letter) => letter.toLocaleUpperCase('es-CO'));
    },
    getAxisMax(values) {
      const maxValue = Math.max(...values.map((value) => Number(value) || 0), 0);
      return maxValue > 0 ? Math.ceil(maxValue * 1.18) : undefined;
    }
  },
  components: {
    VueApexCharts
  }
};
</script>

<style lang="scss" scoped>
.okr-chart-card {
  border: 1px solid #e5e8ef;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.04);
  margin-bottom: 0;
  overflow: visible;
}

::v-deep .apexcharts-canvas,
::v-deep .apexcharts-svg {
  overflow: visible;
}

.okr-chart-card__empty {
  align-items: center;
  color: #6b7280;
  display: flex;
  flex-direction: column;
  font-size: 13px;
  gap: 8px;
  justify-content: center;
  min-height: 340px;
  text-align: center;
}
</style>
